"use client";
import { AppConstant } from "@/const";
import { useHandleUploadFile } from "@/hooks";
import { PlusIcon } from "@/icons";
import { CategoryTypeUploadEnum, IUploadFile } from "@/models";
import { CommonClientUtils } from "@/utils";
import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { DropzoneOptions, useDropzone } from "react-dropzone";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import AppFormControlLayoutPanel from "../AppFormControlLayoutPanel";
import VideoView from "./VideoView";

const AppFormControlUploadVideo = ({
  title,
  control,
  name,
  onUploadSuccessFormCallback,
  onSetValueForm,
  videoRatio = AppConstant.VIDEO_RATIO,
  optionUpload = {},
  categoryType,
  ownerId,
}: AppFormControlUploadVideoProps) => {
  const optionUploadDropzon = {
    accept: AppConstant.ACCEPT_FILE_VIDEO,
    maxSize: AppConstant.MAX_SIZE_UPLOAD,
    ...optionUpload,
  };
  const watcholdFiles: IUploadFile[] = useWatch({
    control,
    name: name,
  });
  const [oldFiles, setOldFiles] = useState<IUploadFile[]>([]);
  useEffect(() => {
    setOldFiles(watcholdFiles);
  }, [watcholdFiles]);
  const { handleUploadFile, handleDeleteFile } = useHandleUploadFile();
  const [fileLoading, setFileLoading] = useState<Array<File>>([]);
  const handleUploadSuccessForm = (files: IUploadFile[]) => {
    onSetValueForm(name, files);
    if (typeof onUploadSuccessFormCallback === "function") {
      onUploadSuccessFormCallback(files);
    }
  };
  const handleDropDocument = async (acceptedFiles: Array<File>) => {
    setFileLoading((prev) => [...acceptedFiles, ...prev]);
    const fileArr = await handleUploadFile(
      acceptedFiles,
      categoryType,
      ownerId
    );

    if (fileArr.length) {
      setOldFiles((prev) => {
        const files = [
          ...(prev || []),
          ...fileArr.map((item) => ({
            ...item,
          })),
        ];
        handleUploadSuccessForm(files);
        return prev;
      });
    }
    setFileLoading((prev) => {
      return prev.slice(0, prev.length - acceptedFiles.length);
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length) {
        CommonClientUtils.handleRejectedUploadFiles(
          rejectedFiles,
          optionUploadDropzon.maxSize || 0
        );
        return;
      }
      handleDropDocument(acceptedFiles);
    },
    ...optionUploadDropzon,
  });

  const handleClearFiles = async (id: string) => {
    const resDelete = await handleDeleteFile(id, categoryType);
    if (resDelete) {
      const newFiles = oldFiles.filter((c) => c.id !== id);
      handleUploadSuccessForm(newFiles);
    }
  };

  return (
    <AppFormControlLayoutPanel
      title={title}
      className="p-0"
      height="fit-content"
      position="relative"
    >
      <Box className="grid grid-cols-12 gap-5">
        {oldFiles.map((item, index) => (
          <Box className="col-span-4" key={item.id + index}>
            <VideoView
              data={item}
              onClearFile={() => handleClearFiles(item.id)}
              videoRatio={videoRatio}
            />
          </Box>
        ))}
        {fileLoading.map((item, index) => (
          <Box className="col-span-4" key={oldFiles.length + index}>
            <VideoView isLoading={true} videoRatio={videoRatio} />
          </Box>
        ))}
        <Box className="col-span-4">
          <Box
            className="w-full rounded-1 border border-[#BDC1CAFF] border-dashed overflow-hidden cursor-pointer"
            sx={{
              aspectRatio: AppConstant.VIDEO_RATIO,
            }}
            {...getRootProps()}
          >
            <Box className="center-root w-full h-full flex items-center justify-center">
              <PlusIcon className="text-primary text-[48px]" />
            </Box>
            <input {...getInputProps()} />
          </Box>
        </Box>
      </Box>
    </AppFormControlLayoutPanel>
  );
};

type AppFormControlUploadVideoProps = {
  title: string;
  name: string;
  control: Control<any>;
  oldFiles?: Array<IUploadFile>;
  onUploadSuccessFormCallback?: (files: Array<IUploadFile>) => void;
  onGetThumbs?: (value: string) => void;
  videoRatio?: number;
  onSetValueForm: UseFormSetValue<any>;
  optionUpload?: Omit<DropzoneOptions, "onDrop">;
  categoryType: CategoryTypeUploadEnum;
  ownerId: string;
};

export default AppFormControlUploadVideo;

// get snapshot from url video
export const getVideoFileSnapshots = async (file: File) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  let snapshots = "";

  // Tạo một đối tượng video
  const video = document.createElement("video");
  video.src = URL.createObjectURL(file);
  video.currentTime = 1;
  video.crossOrigin = "anonymous";

  // Chờ cho video tải xong
  await new Promise((resolve) => {
    video.addEventListener("loadeddata", resolve);
  });

  // Đặt kích thước canvas để phù hợp với video
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  // Vẽ frame hiện tại của video lên canvas
  context?.drawImage(video, 0, 0, canvas.width, canvas.height);

  // Lấy URL hình ảnh từ canvas
  const snapshotUrl = canvas.toDataURL("image/png");

  snapshots = snapshotUrl;

  return snapshots;
};
